import {
  LayoutDashboard, Ticket, Route, Map, Smartphone, ShieldCheck,
  BarChart3, Receipt, Settings, Sparkles, MapPin, AlertTriangle, CheckCircle2,
} from "lucide-react";

const SIDE_ITEMS = [
  { icon: LayoutDashboard, label: "Dashboard", active: true },
  { icon: Ticket, label: "Tickets" },
  { icon: Route, label: "Dispatch" },
  { icon: Map, label: "Map" },
  { icon: Smartphone, label: "Field App" },
  { icon: ShieldCheck, label: "QA Review" },
  { icon: BarChart3, label: "Reports" },
  { icon: Receipt, label: "Billing" },
  { icon: Settings, label: "Admin" },
];

const STATS = [
  { v: "247", l: "Open", color: "text-white" },
  { v: "38", l: "Due Today", color: "text-[#F97316]" },
  { v: "12", l: "Past Due", color: "text-[#EF4444]" },
  { v: "9", l: "High Risk", color: "text-[#EF4444]" },
  { v: "184", l: "Completed", color: "text-[#22C55E]" },
];

const ROWS = [
  { id: "LOC-23914", market: "Atlanta, GA", cust: "Georgia Power", pri: "High", asg: "Marcus T.", sla: "2h 14m", st: "In Progress", risk: 78 },
  { id: "LOC-23921", market: "Nashville, TN", cust: "Piedmont Gas", pri: "Med", asg: "Jordan R.", sla: "5h 02m", st: "Assigned", risk: 42 },
  { id: "LOC-23877", market: "Chicago, IL", cust: "ComEd", pri: "High", asg: "Sarah K.", sla: "Overdue", st: "Past Due", risk: 92 },
  { id: "LOC-23899", market: "Memphis, TN", cust: "MLGW", pri: "Low", asg: "Devon M.", sla: "8h 41m", st: "Assigned", risk: 22 },
  { id: "LOC-23905", market: "Springfield, IL", cust: "Ameren", pri: "Med", asg: "Priya L.", sla: "1d 02h", st: "Complete", risk: 14 },
];

function statusStyle(st) {
  if (st === "Past Due") return "bg-[#EF4444]/15 text-[#EF4444]";
  if (st === "In Progress") return "bg-[#FB923C]/20 text-[#FDBA74]";
  if (st === "Complete") return "bg-[#22C55E]/15 text-[#22C55E]";
  return "bg-white/5 text-white";
}

function priorityStyle(p) {
  if (p === "High") return "text-[#F97316]";
  if (p === "Med") return "text-[#FDBA74]";
  return "text-[#94A3B8]";
}

function riskColor(r) {
  if (r >= 70) return "bg-[#EF4444]";
  if (r >= 40) return "bg-[#F97316]";
  return "bg-[#22C55E]";
}

export default function DashboardMockup({ compact = false }) {
  return (
    <div
      data-testid="dashboard-mockup"
      className="relative w-full glass-strong rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_90px_-20px_rgba(249,115,22,0.6)]"
    >
      {/* Top bar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-[#0a0a0a]/80">
        <span className="h-2.5 w-2.5 rounded-full bg-[#EF4444]/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#F97316]/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#22C55E]/80" />
        <div className="ml-4 text-xs font-mono text-[#94A3B8]">locateos.app / dashboard</div>
      </div>

      <div className="grid grid-cols-[80px_1fr] md:grid-cols-[200px_1fr]">
        {/* Sidebar */}
        <aside className="border-r border-white/5 bg-[#000000]/80 py-4">
          <div className="px-3 mb-4 hidden md:block">
            <div className="text-[10px] uppercase tracking-widest text-[#94A3B8]">Operation</div>
          </div>
          <nav className="space-y-1 px-2">
            {SIDE_ITEMS.map((it) => {
              const Icon = it.icon;
              return (
                <div
                  key={it.label}
                  className={`flex items-center gap-3 px-2.5 py-2 rounded-md text-sm ${
                    it.active ? "bg-[#FB923C]/20 text-white" : "text-[#94A3B8]"
                  }`}
                >
                  <Icon size={16} className={it.active ? "text-[#F97316]" : ""} />
                  <span className="hidden md:inline">{it.label}</span>
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Main */}
        <div className="p-4 md:p-5 space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-5 gap-2">
            {STATS.map((s) => (
              <div key={s.l} className="rounded-lg bg-[#0a0a0a] border border-white/5 px-3 py-2.5">
                <div className={`font-mono font-bold text-lg ${s.color}`}>{s.v}</div>
                <div className="text-[10px] md:text-[11px] text-[#94A3B8] mt-0.5">{s.l}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.7fr_1fr] gap-3">
            {/* Table */}
            <div className="rounded-lg bg-[#0a0a0a]/70 border border-white/5 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5">
                <div className="text-xs font-semibold tracking-wide text-white">Open Tickets</div>
                <div className="text-[10px] font-mono text-[#94A3B8]">{ROWS.length} of 247</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead className="text-[10px] uppercase tracking-wider text-[#94A3B8]">
                    <tr className="border-b border-white/5">
                      <th className="text-left px-3 py-2 font-medium">Ticket</th>
                      <th className="text-left px-2 py-2 font-medium">Market</th>
                      <th className="text-left px-2 py-2 font-medium hidden md:table-cell">Customer</th>
                      <th className="text-left px-2 py-2 font-medium">Pri</th>
                      <th className="text-left px-2 py-2 font-medium hidden md:table-cell">Assigned</th>
                      <th className="text-left px-2 py-2 font-medium">SLA</th>
                      <th className="text-left px-2 py-2 font-medium">Status</th>
                      <th className="text-left px-3 py-2 font-medium">Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ROWS.map((r) => (
                      <tr key={r.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                        <td className="px-3 py-2 font-mono text-white">{r.id}</td>
                        <td className="px-2 py-2 text-[#94A3B8]">{r.market}</td>
                        <td className="px-2 py-2 text-[#94A3B8] hidden md:table-cell">{r.cust}</td>
                        <td className={`px-2 py-2 font-medium ${priorityStyle(r.pri)}`}>{r.pri}</td>
                        <td className="px-2 py-2 text-[#94A3B8] hidden md:table-cell">{r.asg}</td>
                        <td className={`px-2 py-2 font-mono ${r.sla === "Overdue" ? "text-[#EF4444]" : "text-white"}`}>{r.sla}</td>
                        <td className="px-2 py-2">
                          <span className={`status-pill ${statusStyle(r.st)}`}>{r.st}</span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-12 rounded-full bg-white/5 overflow-hidden">
                              <div className={`h-full ${riskColor(r.risk)}`} style={{ width: `${r.risk}%` }} />
                            </div>
                            <span className="font-mono text-[10px] text-white">{r.risk}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right column: mini map + AI */}
            <div className="space-y-3">
              <div className="rounded-lg bg-[#0a0a0a]/70 border border-white/5 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-white">Live Map</div>
                  <div className="text-[10px] font-mono text-[#94A3B8]">SE Region</div>
                </div>
                <div className="relative h-32 rounded-md overflow-hidden bg-gradient-to-br from-[#000000] to-[#0a0a0a] border border-white/5">
                  <div className="absolute inset-0 dot-grid opacity-30" />
                  {/* fake pins */}
                  {[
                    { x: "20%", y: "30%", c: "#F97316" },
                    { x: "55%", y: "45%", c: "#22C55E" },
                    { x: "70%", y: "20%", c: "#EF4444" },
                    { x: "38%", y: "65%", c: "#F97316" },
                    { x: "82%", y: "70%", c: "#FDBA74" },
                    { x: "12%", y: "75%", c: "#22C55E" },
                  ].map((p, i) => (
                    <div
                      key={i}
                      className="absolute"
                      style={{ left: p.x, top: p.y, transform: "translate(-50%, -50%)" }}
                    >
                      <MapPin size={14} color={p.c} fill={p.c} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-gradient-to-br from-[#F97316]/10 to-[#FB923C]/10 border border-[#F97316]/30 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-7 w-7 rounded-md bg-[#F97316]/20 grid place-items-center">
                    <Sparkles size={14} className="text-[#F97316]" />
                  </div>
                  <div className="text-xs font-semibold text-white">LocateOS AI</div>
                  <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E] pulse-dot ml-auto" />
                </div>
                <p className="text-[11px] text-[#94A3B8] leading-relaxed">
                  <span className="text-white">3 tickets</span> are trending high risk due to overdue response windows and missing field evidence.
                </p>
                <div className="mt-2 flex items-center gap-1.5 text-[10px] text-[#F97316]">
                  <AlertTriangle size={12} /> Review now
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
